import React, { useEffect, useState, useMemo } from "react";
import { getByQuery } from "../../api/API";
import LoadingSpinner from "../Generic/LoadingSpinner";
import EventCard from "./EventCard";
import UpcomingOngoingEvents from "./Upcomingevents";
import Herowithvideo from "../Generic/Herowithvideo";

const EventsList = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoData, setVideoData] = useState(null); 

  const [showCompletedCount, setShowCompletedCount] = useState(10);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        // Parallel API calls
        const eventsPromise = getByQuery(
          "events",
          "where[_status][equals]=published&fields=eventId,name,dateFrom,dateTo,price,status,image"
        );

        const videoPromise = getByQuery(
          "media",
          "where[folder.slug]=eventhero&where[mimeType][like]=video/&sort=-createdAt&limit=1&fields=url,thumbnail.url"
        );

        const [eventsData, videoRes] = await Promise.all([eventsPromise, videoPromise]);

        setEvents(eventsData.docs || eventsData);

        if (videoRes?.docs?.length > 0) {
          const video = videoRes.docs[0];
          setVideoData({
            videoSrc: video.url || "",
            videoPoster: video.thumbnail?.url || "",
          });
        }
      } catch (err) {
        console.error("Error fetching events or video:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const normalizeDateUTC = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const getEventStatus = (dateFrom, dateTo) => {
    const todayUTC = normalizeDateUTC(new Date());
    const startUTC = normalizeDateUTC(dateFrom);
    const endUTC = normalizeDateUTC(dateTo);

    if (!startUTC || !endUTC) return "Unknown";
    if (todayUTC < startUTC) return "Upcoming";
    if (todayUTC >= startUTC && todayUTC <= endUTC) return "Ongoing";
    return "Completed";
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const formatTime = (time) =>
    new Date(time).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  const { stats, completedEvents } = useMemo(() => {
    const counts = { total: 0, upcoming: 0, ongoing: 0, completed: 0 };
    const completed = [];

    for (const e of events) {
      const status = getEventStatus(e.dateFrom, e.dateTo);
      counts.total++;
      if (status === "Upcoming") counts.upcoming++;
      else if (status === "Ongoing") counts.ongoing++;
      else if (status === "Completed") {
        counts.completed++;
        completed.push(e);
      }
    }

    return {
      /*
      stats: [
        { value: `${counts.total}+`, label: "Total Events" },
        { value: `${counts.upcoming}+`, label: "Upcoming Events" },
        { value: `${counts.ongoing}+`, label: "Ongoing Events" },
        { value: `${counts.completed}+`, label: "Completed Events" },
      ],
      */
      completedEvents: completed,
    };
  }, [events]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <LoadingSpinner size="4rem" text="Loading Events..." />
      </div>
    );
  }

  if (!events.length) {
    return <p className="text-center my-5">No events available at this time.</p>;
  }

  return (
    <div className="container my-3 events-container">
      {/* Hero section with image instead of video */}
      <Herowithvideo
        title="EVENTS"
        subtitle="Software events are organized gatherings where developers, engineers, and tech enthusiasts come together to learn, share, and collaborate. They include hackathons, conferences, webinars, and workshops focused on the latest tools, trends, and innovations."

        /* ✅ IMAGE VERSION */
        imageSrc="/assets/images/Component 72.png" 

        /* ❌ VIDEO CODE COMMENTED OUT
        videoSrc={videoData?.videoSrc || ""}
        videoPoster={videoData?.videoPoster || ""}
        */

        stats={stats}
      />

      {/* Upcoming/Ongoing */}
      <h3 className="mt-4 mb-3 text-left px-5">Ongoing and Upcoming Events</h3>
      <UpcomingOngoingEvents
        events={events}
        getEventStatus={getEventStatus}
        formatDate={formatDate}
        formatTime={formatTime}
      />

      {/* Completed */}
      <h3 className="mt-5 mb-3 text-left text-danger px-5">Completed Events</h3>
      <div className="row g-4">
        {completedEvents.length > 0 ? (
          completedEvents
            .slice(0, showCompletedCount)
            .map((event) => (
              <EventCard
                key={event.eventId || event.id}
                event={event}
                getEventStatus={getEventStatus}
                formatDate={formatDate}
                formatTime={formatTime}
              />
            ))
        ) : (
          <p className="text-center">No completed events yet.</p>
        )}
      </div>

      {/* Show more button */}
      {showCompletedCount < completedEvents.length && (
        <div className="text-center mt-3">
          <button
            className="btn btn-outline-primary"
            onClick={() =>
              setShowCompletedCount((prev) =>
                Math.min(prev + 10, completedEvents.length)
              )
            }
          >
            Show More Completed Events
          </button>
        </div>
      )}
    </div>
  );
};

export default EventsList;
